import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { AlertCircle, Camera01, Check, RefreshCw01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { DamageZone } from "../types"
import { FlowCta, FlowHeading, PhasePane, QuietLink, StickyActions } from "../flow-chrome"
import { CAPTURE_COPY, phoneLine, shotAnnouncement, shotInstruction, shotTitle, VERDICT_LINE, wrongSideQuestion } from "./capture-copy"
import { exampleFor } from "./examples"
import { localIssue, resultFor } from "./shot-result"
import { useVinStep, VIN_COPY, vinAnnouncement, type VinActions } from "./vin/use-vin-step"
import { useFocusAfterCard, VinCard } from "./vin/vin-card"
import { StandMap, standFor } from "./stand-map"
import type { FrameMetrics } from "./camera/frame-metrics"
import { preparePhoto, type PrepareFailure } from "./uploads/prepare-photo"
import type { ShotUploads } from "./uploads/use-shot-uploads"
import type { CaptureState, PlannedShot, ShotId } from "./types"

// The walk with the phone's own camera — the fallback when the live camera
// can't run (in-app browsers, camera blocked, old phones) and the path a
// person picks with "Use your phone's camera". Each shot: a coach card (real
// example photo + where to stand) → the phone camera → an instant check.
// A good photo moves on by itself; anything flagged asks once, never traps.

const C = CAPTURE_COPY.coach
const AUTO_NEXT_MS = 1400

const FAILURE_COPY: Record<PrepareFailure, string> = {
  "not-image": C.notImage,
  unreadable: C.unreadable,
  "too-large": C.tooLarge,
}

export function NativeCapture({
  plan,
  queue,
  index,
  capture,
  damage,
  uploads,
  onAccept,
  onNext,
  onSkip,
  onReview,
  vin,
}: {
  plan: readonly PlannedShot[]
  queue: readonly ShotId[]
  index: number
  capture: CaptureState
  damage: readonly DamageZone[]
  uploads: Pick<ShotUploads, "submit" | "retry">
  onAccept: (key: string) => void
  onNext: () => void
  onSkip: (id: ShotId) => void
  onReview: () => void
  // The VIN step: a photo of the sticker (read by the server), or type it.
  vin: VinActions
}) {
  const shot = plan.find((s) => s.id === queue[index]) ?? null
  const prev = index > 0 ? plan.find((s) => s.id === queue[index - 1]) ?? null : null
  const inputRef = useRef<HTMLInputElement>(null)
  const [stage, setStage] = useState<"coach" | "result">("coach")
  const vinStep = useVinStep(vin, onNext)
  const vinStepOn = shot?.kind === "sticker"
  const vinCardUp = vinStepOn && vinStep.state.kind !== "idle"
  const vinControls = useRef<HTMLDivElement>(null)
  useFocusAfterCard(vinCardUp, vinStepOn, vinControls)
  const [metrics, setMetrics] = useState<FrameMetrics | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [held, setHeld] = useState(false)
  const takingFor = useRef<PlannedShot | null>(null)
  const currentShotId = useRef<string | null>(null)
  useLayoutEffect(() => {
    currentShotId.current = shot?.id ?? null
  })

  // New shot → back to its coach card.
  const shotId = shot?.id
  useEffect(() => {
    setStage("coach")
    setMetrics(null)
    setError(null)
    setHeld(false)
  }, [shotId])

  const record = shot ? capture.shots[shot.id] : undefined
  const local = localIssue(metrics, shot)
  const result = resultFor(record, local)

  // A clean photo moves on by itself — unless the person touched the card or
  // asked to retake (the picker must never come back to a different shot).
  const onNextRef = useRef(onNext)
  useLayoutEffect(() => {
    onNextRef.current = onNext
  })
  useEffect(() => {
    if (stage !== "result" || held || vinStepOn) return
    if (result.kind !== "good" && result.kind !== "queued") return
    const t = setTimeout(() => onNextRef.current(), AUTO_NEXT_MS)
    return () => clearTimeout(t)
  }, [stage, held, result.kind, vinStepOn])

  if (!shot) return null

  const openCamera = () => {
    if (preparing) return
    setError(null)
    setHeld(true)
    // The photo belongs to the shot that was showing when the camera opened,
    // whatever happens on screen while it's being prepared.
    takingFor.current = shot
    inputRef.current?.click()
  }

  const handleFile = async (file: File | undefined) => {
    const target = takingFor.current
    if (!file || !target) return
    setPreparing(true)
    const prepared = await preparePhoto(file, `${target.id.replace(":", "-")}-${Date.now()}`)
    setPreparing(false)
    const stillHere = currentShotId.current === target.id
    if (!prepared.ok) {
      if (stillHere) setError(FAILURE_COPY[prepared.reason])
      return
    }
    uploads.submit(target.id, prepared.photo.file, { source: "native", planned: target })
    if (target.kind === "sticker") vinStep.photo(prepared.photo.file)
    if (!stillHere) return
    setMetrics(prepared.photo.metrics)
    setHeld(false)
    setStage("result")
  }

  const example = exampleFor(shot)
  const isLast = index >= queue.length - 1

  return (
    <PhasePane key={shot.id} className="pt-1">
      {/* Screen readers hear "Photo 3 of 7: …" on every new shot. */}
      <p className="sr-only" aria-live="polite">
        {shotAnnouncement(shot, index, queue.length)}
      </p>
      <FlowHeading className="text-[26px] font-bold leading-[1.15] tracking-tight text-ink">
        {shotTitle(shot)}
      </FlowHeading>
      <p className="mt-2 text-[16px] leading-relaxed text-zinc-700">{shotInstruction(shot)}</p>

      {/* Portrait, like the phone in their hand; short enough that the result
          line and the buttons always fit on screen. */}
      <div
        className="relative mx-auto mt-4 aspect-[3/4] overflow-hidden rounded-[22px] bg-zinc-900"
        style={{ width: "min(100%, calc(44svh * 0.75))" }}
        onPointerDown={() => stage === "result" && setHeld(true)}
      >
        <div className="absolute inset-0">
          {stage === "coach" ? (
            example ? (
              <>
                <img src={example.src} alt={example.alt} className="absolute inset-0 size-full object-cover" />
                {example.frame && <FrameBrackets frame={example.frame} />}
              </>
            ) : (
              <StickerIllustration />
            )
          ) : (
            record?.previewUrl && (
              <img
                src={record.previewUrl}
                alt={`Your photo: ${shotTitle(shot)}`}
                className="absolute inset-0 size-full object-cover"
              />
            )
          )}

          {stage === "coach" && example && (
            <span className="absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1.5 text-[13px] font-semibold text-white backdrop-blur-sm">
              {C.likeThis}
            </span>
          )}

          {stage === "coach" && (
            <div className="absolute right-3 top-3 w-[84px] rounded-2xl bg-white/95 p-2 shadow-lg">
              <StandMap shot={shot} plan={plan} damage={damage} from={prev ? standFor(prev, plan) : null} />
            </div>
          )}

          {stage === "result" && <ResultBadge result={result} />}
        </div>
      </div>

      <div className="mt-3 min-h-6" aria-live="polite">
        {vinStepOn && <span className="sr-only">{vinAnnouncement(vinStep.state)}</span>}
        {stage === "result" && !vinStepOn && (
          <div data-testid="shot-result" data-result={result.kind}>
            <ResultLine result={result} shot={shot} description={record?.description ?? null} />
          </div>
        )}
        {error && (
          <p role="alert" className="flex items-center gap-2 text-[14px] font-medium text-amber-800">
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            {error}
          </p>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="native-camera-input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ""
          void handleFile(file)
        }}
      />

      <StickyActions className="space-y-2">
        {vinStepOn ? (
          vinCardUp ? (
            <VinCard step={vinStep} tone="light" />
          ) : (
            <div ref={vinControls}>
              {vinStep.state.kind === "idle" && vinStep.state.note && (
                <p className="mb-3 text-[15px] font-medium text-amber-800">
                  {vinStep.state.note}
                </p>
              )}
              <FlowCta onClick={openCamera} loading={preparing}>
                <Camera01 className="size-5" aria-hidden />
                {stage === "result" ? C.retake : C.open}
              </FlowCta>
              <QuietLink onClick={vinStep.type} disabled={preparing} className="mt-1">
                {VIN_COPY.type}
              </QuietLink>
              <QuietLink onClick={() => onSkip(shot.id)} disabled={preparing}>
                {VIN_COPY.skip}
              </QuietLink>
            </div>
          )
        ) : (
        <AnimatePresence mode="wait" initial={false}>
          {stage === "coach" ? (
            <motion.div key="coach" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
              <FlowCta onClick={openCamera} loading={preparing}>
                <Camera01 className="size-5" aria-hidden />
                {C.open}
              </FlowCta>
              <QuietLink onClick={() => onSkip(shot.id)} disabled={preparing} className="mt-1">
                {C.skip}
              </QuietLink>
            </motion.div>
          ) : (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
              <ResultActions
                result={result}
                isLast={isLast}
                busy={preparing}
                onRetake={openCamera}
                onKeep={() => {
                  onAccept(shot.id)
                  onNext()
                }}
                onRetry={() => uploads.retry(shot.id)}
                onNext={onNext}
              />
            </motion.div>
          )}
        </AnimatePresence>
        )}
        {index > 0 && (
          <QuietLink onClick={onReview} disabled={preparing}>
            {C.review}
          </QuietLink>
        )}
      </StickyActions>
    </PhasePane>
  )
}

function ResultBadge({ result }: { result: ReturnType<typeof resultFor> }) {
  if (result.kind === "checking") {
    return (
      <span className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-[13px] font-semibold text-white backdrop-blur-sm">
        <span className="size-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden />
        {C.checking}
      </span>
    )
  }
  if (result.kind === "good") {
    return (
      <motion.span
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 18 }}
        className="absolute bottom-3 left-3 grid size-11 place-items-center rounded-full bg-signal text-white shadow-lg"
      >
        <Check className="size-6" strokeWidth={3} aria-hidden />
      </motion.span>
    )
  }
  return null
}

function ResultLine({
  result,
  shot,
  description,
}: {
  result: ReturnType<typeof resultFor>
  shot: PlannedShot
  description: string | null
}) {
  switch (result.kind) {
    case "good":
      return result.phone ? (
        <p className="text-[15px] font-semibold leading-snug text-ink" data-testid="phone-verdict">
          {phoneLine(shot, result.phone)}
        </p>
      ) : (
        <p className="text-[15px] leading-snug text-zinc-600">
          <span className="font-semibold text-ink">{VERDICT_LINE.good}.</span>
          {description && <> We see: {description.replace(/\.$/, "").toLowerCase()}.</>}
        </p>
      )
    case "issue":
      return <p className="text-[15px] font-medium text-amber-800">{VERDICT_LINE[result.verdict]}</p>
    case "wrong-side":
      return (
        <p className="text-[15px] font-medium text-amber-800" data-testid="phone-verdict">
          {result.phone ? phoneLine(shot, result.phone) : wrongSideQuestion(shot)}
        </p>
      )
    case "queued":
      return <p className="text-[15px] font-medium text-zinc-700">Saved. It will send when you're back online.</p>
    case "failed":
      return <p className="text-[15px] font-medium text-amber-800">{C.failed}</p>
    case "checking":
      return null
  }
}

function ResultActions({
  result,
  isLast,
  busy,
  onRetake,
  onKeep,
  onRetry,
  onNext,
}: {
  result: ReturnType<typeof resultFor>
  isLast: boolean
  // A photo is being prepared: no second retake until it's in.
  busy: boolean
  onRetake: () => void
  onKeep: () => void
  onRetry: () => void
  onNext: () => void
}) {
  const retake = (
    <button
      type="button"
      onClick={onRetake}
      disabled={busy}
      className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl border border-zinc-300 bg-white text-[15px] font-semibold text-ink transition-colors duration-150 hover:border-zinc-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50"
    >
      <RefreshCw01 className="size-4" aria-hidden />
      {C.retake}
    </button>
  )
  // Something to fix that the phone is sure about: retake first, keeping it
  // is the quiet way out.
  if (result.kind === "issue" || (result.kind === "wrong-side" && result.phone)) {
    return (
      <>
        <FlowCta onClick={onRetake} loading={busy}>
          <RefreshCw01 className="size-4" aria-hidden />
          {C.retake}
        </FlowCta>
        <QuietLink onClick={onKeep} className="mt-1">
          {C.keep}
        </QuietLink>
      </>
    )
  }
  if (result.kind === "wrong-side") {
    return (
      <div className="flex gap-2">
        {retake}
        <FlowCta onClick={onKeep} className="flex-1">
          {C.yesRight}
        </FlowCta>
      </div>
    )
  }
  if (result.kind === "failed") {
    return (
      <>
        <div className="flex gap-2">
          {retake}
          <FlowCta onClick={onRetry} className="flex-1">
            {C.retry}
          </FlowCta>
        </div>
        {/* Never stuck on a photo that won't send: fix it later from Review. */}
        <QuietLink onClick={onNext} className="mt-1">
          {C.skipForNow}
        </QuietLink>
      </>
    )
  }
  return (
    <div className="flex gap-2">
      {retake}
      <FlowCta onClick={onNext} className="flex-1">
        {isLast ? CAPTURE_COPY.coach.review : C.next}
      </FlowCta>
    </div>
  )
}

// The same corner brackets the live camera uses, around the car in the example.
function FrameBrackets({ frame }: { frame: { x: number; y: number; w: number; h: number } }) {
  const pad = 0.03
  const style = {
    left: `${(frame.x - pad) * 100}%`,
    top: `${(frame.y - pad) * 100}%`,
    width: `${(frame.w + pad * 2) * 100}%`,
    height: `${(frame.h + pad * 2) * 100}%`,
  }
  const arm = "absolute size-5 border-signal-on-dark"
  return (
    <div className="pointer-events-none absolute" style={style} aria-hidden>
      <span className={cx(arm, "left-0 top-0 rounded-tl-md border-l-[3px] border-t-[3px]")} />
      <span className={cx(arm, "right-0 top-0 rounded-tr-md border-r-[3px] border-t-[3px]")} />
      <span className={cx(arm, "bottom-0 left-0 rounded-bl-md border-b-[3px] border-l-[3px]")} />
      <span className={cx(arm, "bottom-0 right-0 rounded-br-md border-b-[3px] border-r-[3px]")} />
    </div>
  )
}

// The door sticker has no stock photo: a simple drawing of an open door frame
// with its label.
function StickerIllustration() {
  return (
    <svg viewBox="0 0 400 300" className="absolute inset-0 size-full" role="img" aria-label="The sticker on the driver's door frame">
      <rect width="400" height="300" fill="#e4e4e7" />
      <path d="M 70 20 L 210 20 L 210 280 L 70 280 Z" fill="#a1a1aa" />
      <path d="M 210 20 L 330 60 L 330 250 L 210 280 Z" fill="#71717a" />
      <rect x="120" y="120" width="70" height="44" rx="4" fill="#ffffff" stroke="#0c2018" strokeOpacity="0.4" />
      <path d="M 128 132 H 182 M 128 142 H 170 M 128 152 H 176" stroke="#0c2018" strokeOpacity="0.45" strokeWidth="3" strokeLinecap="round" />
      <rect x="112" y="112" width="86" height="60" rx="8" fill="none" stroke="var(--color-signal)" strokeWidth="4" />
    </svg>
  )
}
